/**
 * Пересчёт между временем окончания и длительностью.
 *
 * Один и тот же промежуток удобно задавать по-разному: сон почти всегда
 * мыслится как «встаю в 7:00», обед — как «45 минут», встреча — как «с 10:00
 * до 11:30». Поэтому в интерфейсе есть оба способа, а эти функции держат их
 * согласованными.
 *
 * Отдельная причина вынести расчёт из компонента — переход через полночь.
 * Сон 23:00–07:00 даёт окончание «раньше» начала, и наивная разность выдала бы
 * отрицательную длительность.
 */

import { MINUTES_IN_DAY, type MinutesOfDay } from './datetime';

/**
 * Длительность по времени начала и окончания.
 *
 * Окончание, совпадающее с началом или лежащее раньше него, трактуется как
 * следующие сутки: «с 23:00 до 07:00» — это восемь часов, а не минус
 * шестнадцать. Ровное совпадение даёт полные сутки, а не ноль: нулевой слот
 * бессмысленен, а «сплю ровно 24 часа» — хотя бы выразимо.
 */
export function durationFromEndTime(startMin: MinutesOfDay, endMin: MinutesOfDay): number {
  const diff = (endMin - startMin + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  return diff === 0 ? MINUTES_IN_DAY : diff;
}

/** Время окончания по началу и длительности; за полночь заворачивается. */
export function endTimeFromDuration(startMin: MinutesOfDay, durationMin: number): MinutesOfDay {
  return (((startMin + durationMin) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
}

/** Переходит ли слот на следующий день — для подписи «07:00 следующего дня». */
export function crossesMidnight(startMin: MinutesOfDay, durationMin: number): boolean {
  return startMin + durationMin >= MINUTES_IN_DAY;
}

/** Способ ввода: по времени окончания или по длительности. */
export type DurationInputMode = 'end' | 'duration';
